import React, { useState, useEffect } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  TextField,
  IconButton,
} from "@mui/material";
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Upload as UploadIcon,
  Description as DescriptionIcon,
  PlayArrow as PlayArrowIcon,
} from "@mui/icons-material";
import { stockApi } from "../../../../services/api/plenty/stock";
import { sellerboardApi } from "../../../../services/api/plenty/sellerboard";

const StockManagementSubTab = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Stock Management states
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [calculateLoading, setCalculateLoading] = useState(false);
  const [calculateResult, setCalculateResult] = useState(null);
  const [totalStockValue, setTotalStockValue] = useState(null);
  const [snapshotDate, setSnapshotDate] = useState(
    new Date().toISOString().split("T")[0],
  );

  const [stockSettingsError, setStockSettingsError] = useState("");
  const [stockSettingsSuccess, setStockSettingsSuccess] = useState("");

  // Sellerboard Amazon Articles Processing states
  const [sellerboardProcessLoading, setSellerboardProcessLoading] =
    useState(false);
  const [sellerboardProcessResult, setSellerboardProcessResult] =
    useState(null);

  const loadTotalStockValue = async (force = false) => {
    try {
      if (!force) {
        const cachedData = localStorage.getItem("totalStockValueData");
        if (cachedData) {
          setTotalStockValue(JSON.parse(cachedData));
          return;
        }
      }
      const response = await stockApi.getTotalStockValue();
      setTotalStockValue(response.data);
      localStorage.setItem(
        "totalStockValueData",
        JSON.stringify(response.data),
      );
    } catch (error) {
      console.error("Failed to load total stock value:", error);
    }
  };

  useEffect(() => {
    loadTotalStockValue();
  }, []);

  // Stock Management handlers
  const handleImportFromShoppingTable = async () => {
    setImportLoading(true);
    setImportResult(null);
    setStockSettingsError("");

    try {
      const response = await stockApi.importFromShoppingTable({
        skip_existing: false,
      });
      setImportResult(response.data);
      setStockSettingsSuccess(
        `Import completed: ${response.data.created} created, ${response.data.updated || 0} updated`,
      );
      setTimeout(() => setStockSettingsSuccess(""), 5000);
    } catch (error) {
      console.error("Failed to import from shopping_table:", error);
      setStockSettingsError(getErrorMessage(error));
      setImportResult({ error: getErrorMessage(error) });
    } finally {
      setImportLoading(false);
    }
  };

  const handleCalculateStockValues = async (dryRun = false) => {
    setCalculateLoading(true);
    setCalculateResult(null);
    setStockSettingsError("");

    try {
      const response = await stockApi.calculateStockValues({
        min_stock: 1,
        dry_run: dryRun,
        snapshot_date: snapshotDate,
      });
      setCalculateResult(response.data);

      if (!dryRun) {
        setStockSettingsSuccess(
          `Stock values ${dryRun ? "calculated" : "updated"}: ${
            response.data.processed
          } articles processed`,
        );

        // Refresh total stock value after calculation
        await loadTotalStockValue(true);
      }

      setTimeout(() => setStockSettingsSuccess(""), 5000);
    } catch (error) {
      console.error("Failed to calculate stock values:", error);
      setStockSettingsError(getErrorMessage(error));
      setCalculateResult({ error: getErrorMessage(error) });
    } finally {
      setCalculateLoading(false);
    }
  };

  const downloadResultAsJson = (data, filename) => {
    const jsonStr = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonStr], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${filename}_${
      new Date().toISOString().split("T")[0]
    }.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleProcessSellerboardAmazonArticles = async () => {
    setSellerboardProcessLoading(true);
    setSellerboardProcessResult(null);
    setStockSettingsError("");

    try {
      const response =
        await sellerboardApi.process_report_to_amazon_articles("Lager");
      setSellerboardProcessResult(response.data);
      setStockSettingsSuccess(
        "Sellerboard Amazon articles processed successfully",
      );
      setTimeout(() => setStockSettingsSuccess(""), 5000);
    } catch (error) {
      console.error("Failed to process Sellerboard Amazon articles:", error);
      setStockSettingsError(getErrorMessage(error));
      setSellerboardProcessResult({ error: getErrorMessage(error) });
    } finally {
      setSellerboardProcessLoading(false);
    }
  };

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Total Stock Value Card */}
        {totalStockValue && (
          <Grid item xs={12}>
            <Card sx={{ backgroundColor: "#2e7d32", color: "white" }}>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
                    Total Stock Value
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => loadTotalStockValue(true)}
                    sx={{ color: "white" }}
                    title="Force Reload"
                  >
                    <RefreshIcon />
                  </IconButton>
                </Box>
                <Typography variant="h4" sx={{ mb: 1 }}>
                  <Box
                    component="span"
                    sx={{
                      fontSize: "0.5em",
                      opacity: 0.8,
                      verticalAlign: "middle",
                      mr: 1,
                      textTransform: "uppercase",
                    }}
                  >
                    Combined:
                  </Box>
                  €
                  {(
                    totalStockValue.combined_stock_value ??
                    totalStockValue.total_stock_value
                  )?.toLocaleString()}
                </Typography>

                <Box sx={{ display: "flex", gap: 4, mb: 1 }}>
                  <Typography variant="body2">
                    <strong>Stock:</strong> €
                    {totalStockValue.total_stock_value?.toLocaleString()} (
                    {totalStockValue.total_stock_units} units)
                  </Typography>
                  <Typography variant="body2">
                    <strong>Transit:</strong> €
                    {totalStockValue.total_transit_stock_value?.toLocaleString() ||
                      0}{" "}
                    ({totalStockValue.total_transit_units || 0} units)
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
                  {totalStockValue.articles_with_value} articles processed
                </Typography>

                {/* Breakdown by source */}
                {totalStockValue.by_source && (
                  <Box
                    sx={{
                      mt: 2,
                      display: "flex",
                      gap: 3,
                      flexWrap: "wrap",
                    }}
                  >
                    {/* Plenty */}
                    <Box
                      sx={{
                        backgroundColor: "rgba(255, 255, 255, 0.15)",
                        padding: 1.5,
                        borderRadius: 1,
                        flex: "1 1 auto",
                        minWidth: "150px",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ display: "block", opacity: 0.9 }}
                      >
                        Plenty
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          color: "rgb(25, 118, 210)",
                          fontWeight: "bold",
                        }}
                      >
                        €
                        {(
                          totalStockValue.by_source.plenty
                            ?.combined_stock_value ??
                          totalStockValue.by_source.plenty?.stock_value
                        )?.toLocaleString() || "0"}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ display: "block", opacity: 0.8 }}
                      >
                        Stock: €
                        {totalStockValue.by_source.plenty?.stock_value?.toLocaleString() ||
                          "0"}{" "}
                        ({totalStockValue.by_source.plenty?.stock_units || 0}u)
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ display: "block", opacity: 0.8 }}
                      >
                        Transit: €
                        {totalStockValue.by_source.plenty?.transit_stock_value?.toLocaleString() ||
                          "0"}{" "}
                        ({totalStockValue.by_source.plenty?.transit_units || 0}
                        u)
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ opacity: 0.8, mt: 0.5, display: "block" }}
                      >
                        {totalStockValue.by_source.plenty?.article_count || 0}{" "}
                        articles
                      </Typography>
                    </Box>

                    {/* Amazon */}
                    <Box
                      sx={{
                        backgroundColor: "rgba(255, 255, 255, 0.15)",
                        padding: 1.5,
                        borderRadius: 1,
                        flex: "1 1 auto",
                        minWidth: "150px",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ display: "block", opacity: 0.9 }}
                      >
                        Amazon
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          color: "rgb(245, 0, 87)",
                          fontWeight: "bold",
                        }}
                      >
                        €
                        {(
                          totalStockValue.by_source.amazon
                            ?.combined_stock_value ??
                          totalStockValue.by_source.amazon?.stock_value
                        )?.toLocaleString() || "0"}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ display: "block", opacity: 0.8 }}
                      >
                        Stock: €
                        {totalStockValue.by_source.amazon?.stock_value?.toLocaleString() ||
                          "0"}{" "}
                        ({totalStockValue.by_source.amazon?.stock_units || 0}u)
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ display: "block", opacity: 0.8 }}
                      >
                        Transit: €
                        {totalStockValue.by_source.amazon?.transit_stock_value?.toLocaleString() ||
                          "0"}{" "}
                        ({totalStockValue.by_source.amazon?.transit_units || 0}
                        u)
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ opacity: 0.8, mt: 0.5, display: "block" }}
                      >
                        {totalStockValue.by_source.amazon?.article_count || 0}{" "}
                        articles
                      </Typography>
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Import from shopping_table Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Import Purchase History
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Import purchase data from the shopping_table database (cleaned
                shopping sheet clone)
              </Typography>

              <Button
                variant="contained"
                color="primary"
                onClick={handleImportFromShoppingTable}
                disabled={importLoading}
                startIcon={
                  importLoading ? (
                    <CircularProgress size={20} />
                  ) : (
                    <UploadIcon />
                  )
                }
                fullWidth
              >
                {importLoading ? "Importing..." : "Import from Shopping Table"}
              </Button>

              {importResult && (
                <Box sx={{ mt: 2 }}>
                  <Paper
                    sx={{
                      p: 2,
                      backgroundColor: importResult.error
                        ? "#ffebee"
                        : "#e8f5e9",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight="bold">
                        Import Result
                      </Typography>
                      <Button
                        size="small"
                        onClick={() =>
                          downloadResultAsJson(importResult, "import_result")
                        }
                        startIcon={<DescriptionIcon />}
                      >
                        Download
                      </Button>
                    </Box>

                    {importResult.error ? (
                      <Typography variant="body2" color="error">
                        Error: {importResult.error}
                      </Typography>
                    ) : (
                      <>
                        <Typography variant="body2">
                          Created: {importResult.created} | Updated:{" "}
                          {importResult.updated ?? 0} | Skipped:{" "}
                          {importResult.skipped}
                        </Typography>
                        <Typography variant="body2">
                          Total Rows: {importResult.total_rows}
                          {importResult.source
                            ? ` · Source: ${importResult.source}`
                            : ""}
                        </Typography>
                        {importResult.errors &&
                          importResult.errors.length > 0 && (
                            <Box
                              sx={{
                                mt: 1,
                                maxHeight: 200,
                                overflow: "auto",
                              }}
                            >
                              <Typography variant="caption" color="error">
                                Errors ({importResult.errors.length}
                                ):
                              </Typography>
                              {importResult.errors
                                .slice(0, 5)
                                .map((err, idx) => (
                                  <Typography
                                    key={idx}
                                    variant="caption"
                                    display="block"
                                    color="error"
                                  >
                                    • {err}
                                  </Typography>
                                ))}
                              {importResult.errors.length > 5 && (
                                <Typography variant="caption" color="error">
                                  ... and {importResult.errors.length - 5} more
                                </Typography>
                              )}
                            </Box>
                          )}
                      </>
                    )}
                  </Paper>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Calculate Stock Values Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Calculate Stock Values
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Calculate FIFO stock values for all articles. Re-calculating for
                a past date will also regenerate the evaluation file for that
                date.
              </Typography>

              <TextField
                label="Snapshot Date"
                type="date"
                size="small"
                fullWidth
                value={snapshotDate}
                onChange={(e) => setSnapshotDate(e.target.value)}
                sx={{ mb: 2, maxWidth: 200 }}
                slotProps={{
                  inputLabel: { shrink: true },
                }}
              />

              <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => handleCalculateStockValues(true)}
                  disabled={calculateLoading}
                  startIcon={
                    calculateLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      <PlayArrowIcon />
                    )
                  }
                  sx={{ flex: 1 }}
                >
                  Dry Run
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  onClick={() => handleCalculateStockValues(false)}
                  disabled={calculateLoading}
                  startIcon={
                    calculateLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  sx={{ flex: 1 }}
                >
                  Calculate & Save
                </Button>
              </Box>

              {calculateResult && (
                <Box sx={{ mt: 2 }}>
                  <Paper
                    sx={{
                      p: 2,
                      backgroundColor: calculateResult.error
                        ? "#ffebee"
                        : "#e3f2fd",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight="bold">
                        Calculation Result
                      </Typography>
                      <Button
                        size="small"
                        onClick={() =>
                          downloadResultAsJson(
                            calculateResult,
                            "calculation_result",
                          )
                        }
                        startIcon={<DescriptionIcon />}
                      >
                        Download
                      </Button>
                    </Box>

                    {calculateResult.error ? (
                      <Typography variant="body2" color="error">
                        Error: {calculateResult.error}
                      </Typography>
                    ) : (
                      <>
                        <Typography variant="body2">
                          Processed: {calculateResult.processed} | Updated:{" "}
                          {calculateResult.updated}
                        </Typography>
                        <Typography variant="body2">
                          Skipped: {calculateResult.skipped}
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          sx={{ mt: 1 }}
                        >
                          Total Stock Value: €
                          {calculateResult.total_stock_value?.toLocaleString()}
                        </Typography>
                        {calculateResult.dry_run && (
                          <Chip
                            label="DRY RUN - No changes saved"
                            size="small"
                            color="warning"
                            sx={{ mt: 1 }}
                          />
                        )}
                      </>
                    )}
                  </Paper>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Process Sellerboard Amazon Articles Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Process Sellerboard Amazon Articles
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Process Lager report from Sellerboard to populate Amazon
                articles
              </Typography>

              <Button
                variant="contained"
                color="primary"
                onClick={handleProcessSellerboardAmazonArticles}
                disabled={sellerboardProcessLoading}
                startIcon={
                  sellerboardProcessLoading ? (
                    <CircularProgress size={20} />
                  ) : (
                    <PlayArrowIcon />
                  )
                }
                fullWidth
              >
                {sellerboardProcessLoading
                  ? "Processing..."
                  : "Process Lager Report"}
              </Button>

              {sellerboardProcessResult && (
                <Box sx={{ mt: 2 }}>
                  <Paper
                    sx={{
                      p: 2,
                      backgroundColor: sellerboardProcessResult.error
                        ? "#ffebee"
                        : "#e8f5e9",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight="bold">
                        Processing Result
                      </Typography>
                      <Button
                        size="small"
                        onClick={() =>
                          downloadResultAsJson(
                            sellerboardProcessResult,
                            "sellerboard_process_result",
                          )
                        }
                        startIcon={<DescriptionIcon />}
                      >
                        Download JSON
                      </Button>
                    </Box>

                    {sellerboardProcessResult.error ? (
                      <Typography variant="body2" color="error">
                        Error: {sellerboardProcessResult.error}
                      </Typography>
                    ) : (
                      <>
                        {sellerboardProcessResult.message && (
                          <Typography variant="body2">
                            {sellerboardProcessResult.message}
                          </Typography>
                        )}
                        {sellerboardProcessResult.created !== undefined && (
                          <Typography variant="body2">
                            Created: {sellerboardProcessResult.created}
                          </Typography>
                        )}
                        {sellerboardProcessResult.updated !== undefined && (
                          <Typography variant="body2">
                            Updated: {sellerboardProcessResult.updated}
                          </Typography>
                        )}
                        {sellerboardProcessResult.skipped !== undefined && (
                          <Typography variant="body2">
                            Skipped: {sellerboardProcessResult.skipped}
                          </Typography>
                        )}
                      </>
                    )}
                  </Paper>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Refresh Total Value Button */}
        <Grid item xs={12}>
          <Button
            variant="outlined"
            onClick={loadTotalStockValue}
            startIcon={<RefreshIcon />}
          >
            Refresh Total Stock Value
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default StockManagementSubTab;
